import { gql, type TypedDocumentNode } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { SideNavItem } from '@repo/shared-client/components/blocks'
import {
  Button,
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Separator,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/shared-client/components/ui'
import { cn } from '@repo/shared-client/lib/utils'
import { UserRole } from '@repo/shared-common'
import { Link, useLocation, useNavigate, useRouter } from '@tanstack/react-router'
import LogoFull from '@web/src/components/primatives/logo-full/logo-full'
import LogoSymbol from '@web/src/components/primatives/logo-symbol/logo-symbol'
import NotificationIndicator from '@web/src/features/notifications/notification-indicator/notification-indicator'
import NSFWToggle from '@web/src/features/nsfw-toggle/nsfw-toggle'
import WhatsNewBanner from '@web/src/features/whats-new/whats-new-banner/whats-new-banner'
import { useTranslation } from '@web/src/lib/intl/react'
import { isSentryFeedbackEnabled, openSentryFeedback } from '@web/src/lib/sentry/open-sentry-feedback'
import {
  CalendarDaysIcon,
  CalendarIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  HouseIcon,
  ImagesIcon,
  LayersIcon,
  LibraryIcon,
  LogOutIcon,
  MapPinIcon,
  MenuIcon,
  MessageSquarePlusIcon,
  MessagesSquareIcon,
  MonitorCogIcon,
  StoreIcon,
  UsersRoundIcon,
  XIcon,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import SFProfileBanner from 'src/components/primatives/sf-profile-banner/sf-profile-banner'
import { useColorScheme } from 'src/contexts/color-scheme-context'
import { useSFContext } from 'src/contexts/sf-context'
import ChatIndicator from 'src/features/chat/chat-indicator/chat-indicator'
import PwaInstallButton from 'src/features/pwa-install/pwa-install-button'
import { type ActiveThemeName, availableThemes, getCurrentThemeName, isThemeName } from 'src/theme'
import type {
  AssumeRoleMutation,
  AssumeRoleMutationVariables,
  UpdateThemeSettingsMutation,
  UpdateThemeSettingsMutationVariables,
} from 'types/graphql'
import Cookies from 'universal-cookie'
import { useAuth } from '../../hooks/use-auth'
import ChristmasLightEffect from '../holidays/christmas-light-effect/christmas-light-effect'
import FogEffect from '../holidays/fog-effect/fog-effect'
import SnowEffect from '../holidays/snow-effect/snow-effect'
import SpiderWalkingEffect from '../holidays/spider-walking-effect/spider-walking-effect'

export const getSeasonalCookie = (cookieName: string): boolean => {
  const cookies = new Cookies()
  const value = cookies.get(cookieName)

  // Default to true (effects ON by default)
  if (value === undefined) {
    return true
  }
  if (typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'string') {
    return value === 'true'
  }
  return Boolean(value)
}

export const setSeasonalCookie = (cookieName: string, value: boolean, activeTheme?: ActiveThemeName) => {
  const cookies = new Cookies()
  const now = new Date()
  let expiration = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000) // 30 days
  const currentYear = now.getUTCFullYear()
  const themeName = activeTheme ?? getCurrentThemeName(now)

  switch (themeName) {
    case 'halloween':
      expiration = new Date(Date.UTC(currentYear, 10, 1)) // Nov 1st 00:00 UTC
      break
    case 'christmas':
      expiration = new Date(Date.UTC(currentYear + 1, 0, 1)) // Jan 1st 00:00 UTC
      break
    default:
      break
  }

  cookies.set(cookieName, value.toString(), {
    path: '/',
    expires: expiration,
  })
}

export const ASSUME_ROLE: TypedDocumentNode<AssumeRoleMutation, AssumeRoleMutationVariables> = gql`
  mutation AssumeRole($role: String!) {
    assumeRole(role: $role)
  }
`

const UPDATE_THEME_SETTINGS: TypedDocumentNode<UpdateThemeSettingsMutation, UpdateThemeSettingsMutationVariables> = gql`
  mutation UpdateThemeSettings($profileId: String!, $theme: String!, $seasonalThemesEnabled: Boolean!) {
    updateThemeSettings(profileId: $profileId, theme: $theme, seasonalThemesEnabled: $seasonalThemesEnabled) {
      theme
      seasonalThemesEnabled
    }
  }
`

type CenterNavIconProps = {
  tooltip: string
  icon: ReactNode
  route: string | string[]
  additionalControls?: ReactNode
}

const CenterNavIcon = ({ tooltip, icon, route, additionalControls }: CenterNavIconProps) => {
  const navigate = useNavigate()
  const location = useLocation()

  const routePaths = Array.isArray(route) ? route : [route]

  const hasActiveRoute = routePaths.some((r) => {
    const currentPath = location.pathname
    return currentPath === r || currentPath.startsWith(`${r}/`)
  })

  const navigationRoute = routePaths[0]

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            aria-label={tooltip}
            className={cn(
              'flex h-12 w-[50px] shrink items-center justify-center border-b-[3px] border-solid bg-transparent p-0 transition-colors',
              hasActiveRoute ? 'border-primary' : 'border-border',
              'hover:border-primary'
            )}
            onClick={() => navigate({ to: navigationRoute })}
            type="button"
          />
        }
      >
        <span className="flex h-12 w-full items-center justify-center text-foreground [&>svg]:size-7">{icon}</span>
        {additionalControls}
      </TooltipTrigger>
      <TooltipContent side="bottom">{tooltip}</TooltipContent>
    </Tooltip>
  )
}

const NavBarAuthenticated = ({ onOpen, className }: { onOpen?: () => void; className?: string }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const router = useRouter()
  const {
    currentUser,
    currentUserRole,
    hasPermission,
    isAtLeastRole,
    logOut,
    isAuthenticated,
    isImpersonating,
    refreshSession,
    stopImpersonating,
  } = useAuth()
  const { displayBackButton } = useSFContext()

  let roleTitle = 'member'

  const [assumeRole] = useMutation(ASSUME_ROLE, {
    onCompleted: () => {
      window.location.reload()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const [updateThemeSettings] = useMutation(UPDATE_THEME_SETTINGS, {
    onCompleted: async () => {
      toast.success(t('Theme Updated'))
      await refreshSession()
      await router.invalidate()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const isThemeAdmin = isAtLeastRole(UserRole.ADMIN)

  const handleMenuAction = (action: 'profile' | 'profileEdit' | 'account' | 'signout' | 'exitImpersonation') => {
    if (action === 'profile') {
      navigate({ to: `/profile/${currentUser?.profile?.id}` })
    } else if (action === 'profileEdit') {
      navigate({ to: `/profile/${currentUser?.profile?.id}/edit/general` })
    } else if (action === 'account') {
      navigate({ to: `/account/${currentUser?.profile?.id}/general` })
    } else if (action === 'exitImpersonation') {
      stopImpersonating.mutate(undefined, {
        onSuccess: () => {
          toast.success(t('Exited impersonation'))
          window.location.assign('/admin/accounts')
        },
        onError: (error) => {
          toast.error(error instanceof Error ? error.message : t('Failed to exit impersonation'))
        },
      })
    } else if (action === 'signout') {
      logOut.mutate()
    }
  }

  const handleAssumeRole = (role: string) => {
    assumeRole({ variables: { role } })
  }

  const handleThemeChange = (theme: string) => {
    const profileId = currentUser?.profile?.id
    if (!profileId || theme === currentUser?.theme) {
      return
    }

    updateThemeSettings({
      variables: {
        profileId,
        theme,
        seasonalThemesEnabled: currentUser?.seasonalThemesEnabled ?? true,
      },
    })
  }

  const handleSeasonalThemesChange = (enabled: boolean) => {
    const profileId = currentUser?.profile?.id
    if (!profileId || enabled === (currentUser?.seasonalThemesEnabled ?? true)) {
      return
    }

    updateThemeSettings({
      variables: {
        profileId,
        theme: currentUser?.theme ?? 'dark',
        seasonalThemesEnabled: enabled,
      },
    })
  }

  if (currentUserRole === UserRole.ADMIN) {
    roleTitle = 'admin'
  } else if (currentUserRole === UserRole.MODERATOR) {
    roleTitle = 'moderator'
  } else if (currentUserRole === UserRole.SUPPORTER) {
    roleTitle = 'supporter'
  } else if (currentUserRole === UserRole.TRADESHOW) {
    roleTitle = 'tradeshow'
  } else if (currentUserRole === UserRole.PREVIEW) {
    roleTitle = 'preview member'
  }

  return (
    <div
      className={cn(
        'mx-auto grid h-14 w-full max-w-full grid-cols-[1fr_auto] items-center px-2 lg:grid-cols-4',
        className
      )}
    >
      <div className="flex min-w-0 items-center gap-2 overflow-hidden">
        {displayBackButton && isAuthenticated && (
          <Button
            aria-label={t('Go back')}
            className="lg:hidden"
            onClick={() => router.history.back()}
            size="icon"
            type="button"
            variant="ghost"
          >
            <ChevronLeftIcon className="size-8" />
          </Button>
        )}

        <div className="min-[400px]:hidden">
          <Link className="relative z-10 inline-flex cursor-pointer touch-manipulation" to="/">
            <LogoSymbol imageClassName="@[1000px]:hidden h-13 mb-1 w-auto" />
          </Link>
        </div>

        <div className="hidden min-[400px]:block">
          <LogoFull justify="flex-start" size="sm" />
        </div>
      </div>

      <div className="col-span-2 hidden min-w-0 items-center justify-center gap-2 lg:flex">
        <CenterNavIcon icon={<HouseIcon />} route="/feed" tooltip={t('Home')} />
        <CenterNavIcon icon={<UsersRoundIcon />} route="/profile/list" tooltip={t('Profile Search')} />
        <CenterNavIcon icon={<ImagesIcon />} route="/gallery" tooltip={t('Gallery')} />
        <CenterNavIcon icon={<MapPinIcon />} route="/place" tooltip={t('Places')} />
        <CenterNavIcon icon={<StoreIcon />} route="/vendor-market" tooltip={t('Vendor Market')} />
        <CenterNavIcon icon={<LibraryIcon />} route="/publication/list" tooltip={t('Cliterature')} />
        <CenterNavIcon icon={<CalendarDaysIcon />} route="/event" tooltip={t('Events')} />
        <CenterNavIcon icon={<LayersIcon />} route="/group/list" tooltip={t('Groups')} />
      </div>

      <div className="flex min-w-0 items-center gap-1 justify-self-end lg:gap-2">
        {currentUser?.profile?.settingNSFW !== 'EXPLICIT' && <NSFWToggle />}
        <ChatIndicator />
        <NotificationIndicator />
        <div className="relative flex items-center pr-1">
          <DropdownMenu>
            <DropdownMenuTrigger
              className="shadow-none transition-all duration-300"
              render={
                <button
                  className="inline-flex cursor-pointer items-center gap-0 rounded-md border-0 bg-transparent p-0 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  type="button"
                />
              }
            >
              <span className="inline-flex shrink-0">
                <SFProfileBanner disableLinks hideProfileName profile={currentUser?.profile} size="sm" />
              </span>
              <div className="ml-2 hidden flex-col items-start gap-px md:flex">
                <span className="line-clamp-1 max-w-35 text-left text-sm">{currentUser?.profile?.name}</span>
                <span className="text-muted-foreground text-xs capitalize">
                  {isImpersonating ? t('Impersonating') : roleTitle}
                </span>
              </div>
              <ChevronDownIcon aria-hidden className="hidden size-4 shrink-0 md:block" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="bottom" sideOffset={4}>
              <DropdownMenuItem onClick={() => handleMenuAction('profile')}>{t('Profile')}</DropdownMenuItem>
              <DropdownMenuItem
                disabled={!hasPermission('profile:update')}
                onClick={() => handleMenuAction('profileEdit')}
              >
                {t('Edit Profile')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleMenuAction('account')}>{t('Account')}</DropdownMenuItem>
              {currentUser?.canAssumeRole && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>{t('Assume Role')}</DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="min-w-44" side="left" sideOffset={4}>
                      <DropdownMenuItem onClick={() => handleAssumeRole('member')}>{t('Member')}</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleAssumeRole('preview')}>
                        {t('Preview Member')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleAssumeRole('supporter')}>
                        {t('Supporter')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleAssumeRole('tradeshow')}>
                        {t('Tradeshow')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleAssumeRole('moderator')}>
                        {t('Moderator')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleAssumeRole('admin')}>{t('Admin')}</DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                </>
              )}
              {isThemeAdmin && (
                <>
                  {!currentUser?.canAssumeRole && <DropdownMenuSeparator />}
                  <DropdownMenuSeparator />
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>{t('Theme')}</DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="min-w-44" side="left" sideOffset={4}>
                      <DropdownMenuRadioGroup onValueChange={handleThemeChange} value={currentUser?.theme ?? 'dark'}>
                        {availableThemes(true, t).map((theme) => (
                          <DropdownMenuRadioItem key={theme.name} value={theme.name}>
                            {theme.label}
                          </DropdownMenuRadioItem>
                        ))}
                      </DropdownMenuRadioGroup>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                  <DropdownMenuItem
                    onClick={() => handleSeasonalThemesChange(!(currentUser?.seasonalThemesEnabled ?? true))}
                  >
                    <span className="flex-1">{t('Seasonal themes')}</span>
                    <Switch
                      checked={currentUser?.seasonalThemesEnabled ?? true}
                      className="pointer-events-none"
                      tabIndex={-1}
                    />
                  </DropdownMenuItem>
                </>
              )}
              {isImpersonating && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => handleMenuAction('exitImpersonation')}>
                    {t('Exit Impersonation')}
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleMenuAction('signout')}>{t('Sign out')}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <Button aria-label={t('Open menu')} onClick={onOpen} size="icon" type="button" variant="outline">
          <MenuIcon />
        </Button>
      </div>
    </div>
  )
}

const NavBarUnauthenticated = ({ className }: { className?: string }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const router = useRouter()
  const { displayBackButton } = useSFContext()
  const { isAuthenticated } = useAuth()

  return (
    <div className={cn('mx-auto grid h-14 w-full max-w-full grid-cols-9 items-center px-2', className)}>
      <div className="col-span-6 flex items-center gap-2 md:col-span-4">
        {displayBackButton && isAuthenticated && (
          <Button
            aria-label={t('Go back')}
            onClick={() => router.history.back()}
            size="icon-lg"
            type="button"
            variant="ghost"
          >
            <ChevronLeftIcon className="size-8" />
          </Button>
        )}
        <LogoFull justify="flex-start" size="sm" />
      </div>

      <div className="col-span-3 flex justify-end justify-self-end md:col-span-5">
        <div className="flex items-center gap-2">
          <Button onClick={() => navigate({ to: '/auth/login' })} type="button">
            {t('Login')}
          </Button>

          <Button className="hidden md:inline-flex" onClick={() => navigate({ to: '/auth/signup' })} type="button">
            {t('Join For Free')}
          </Button>

          <Button className="md:hidden" onClick={() => navigate({ to: '/auth/signup' })} type="button">
            {t('Join')}
          </Button>
        </div>
      </div>
    </div>
  )
}

const SeasonalEffects = ({
  showFog,
  showSpider,
  showSnowflake,
  showChristmasLights,
}: {
  showFog: boolean
  showSpider: boolean
  showSnowflake: boolean
  showChristmasLights: boolean
}) => {
  const { themeName } = useColorScheme()

  return (
    <>
      {isThemeName(themeName, 'christmas') && (
        <>
          {showSnowflake && <SnowEffect />}
          {showChristmasLights && <ChristmasLightEffect />}
        </>
      )}
      {isThemeName(themeName, 'halloween') && (
        <>
          {showFog && <FogEffect />}
          {showSpider && <SpiderWalkingEffect />}
        </>
      )}
    </>
  )
}

const SeasonalEffectsControls = ({
  spiderEnabled,
  fogEnabled,
  snowflakeEnabled,
  christmasLightsEnabled,
  onSpiderToggle,
  onFogToggle,
  onSnowflakeToggle,
  onChristmasLightsToggle,
}: {
  spiderEnabled: boolean
  fogEnabled: boolean
  snowflakeEnabled: boolean
  christmasLightsEnabled: boolean
  onSpiderToggle: (enabled: boolean) => void
  onFogToggle: (enabled: boolean) => void
  onSnowflakeToggle: (enabled: boolean) => void
  onChristmasLightsToggle: (enabled: boolean) => void
}) => {
  const { t } = useTranslation()
  const { themeName } = useColorScheme()

  if (!(isThemeName(themeName, 'christmas') || isThemeName(themeName, 'halloween'))) {
    return null
  }

  return (
    <div className="flex w-full flex-col items-stretch gap-3 pb-4">
      <p className="font-semibold text-muted-foreground text-sm">{t('Seasonal Effects')}</p>
      {isThemeName(themeName, 'christmas') && (
        <>
          <div className="flex w-full items-center justify-between gap-2">
            <span className="text-sm">{t('Show Spicy Snow Flakes')}</span>
            <Switch checked={snowflakeEnabled} onCheckedChange={(checked) => onSnowflakeToggle(checked)} />
          </div>
          <div className="flex w-full items-center justify-between gap-2">
            <span className="text-sm">{t('Show Christmas Lights')}</span>
            <Switch checked={christmasLightsEnabled} onCheckedChange={(checked) => onChristmasLightsToggle(checked)} />
          </div>
        </>
      )}

      {isThemeName(themeName, 'halloween') && (
        <>
          <div className="flex w-full items-center justify-between gap-2">
            <span className="text-sm">{t('Show Spiders')}</span>
            <Switch checked={spiderEnabled} onCheckedChange={(checked) => onSpiderToggle(checked)} />
          </div>
          <div className="flex w-full items-center justify-between gap-2">
            <span className="text-sm">{t('Show Halloween Fog')}</span>
            <Switch checked={fogEnabled} onCheckedChange={(checked) => onFogToggle(checked)} />
          </div>
        </>
      )}
    </div>
  )
}

const MainNavBar = () => {
  const { t } = useTranslation()
  const { themeName } = useColorScheme()
  const { isAuthenticated, currentUser, hasPermission, isAtLeastRole, logOut } = useAuth()
  const [drawerControl, setDrawerControl] = useState(false)
  const [feedbackMenuOpen, setFeedbackMenuOpen] = useState(false)
  const [showFog, setShowFog] = useState(() => getSeasonalCookie('SeasonalFogEffect'))
  const [showSpider, setShowSpider] = useState(() => getSeasonalCookie('SeasonalSpiderWalking'))
  const [showSnowflake, setShowSnowflake] = useState(() => getSeasonalCookie('SeasonalSnowflake'))
  const [showChristmasLights, setShowChristmasLights] = useState(() => getSeasonalCookie('SeasonalChristmasLights'))

  const closeDrawer = () => {
    setDrawerControl(false)
    setFeedbackMenuOpen(false)
  }

  return (
    <>
      {isAuthenticated && (
        <SeasonalEffects
          showChristmasLights={showChristmasLights}
          showFog={showFog}
          showSnowflake={showSnowflake}
          showSpider={showSpider}
        />
      )}
      <header className="fixed top-0 z-50 w-full border-border/40 border-b bg-muted shadow-sm">
        {!!isAuthenticated && <NavBarAuthenticated onOpen={() => setDrawerControl(true)} />}
        {!isAuthenticated && <NavBarUnauthenticated />}
      </header>
      <div className="h-14" />
      <WhatsNewBanner />

      <Drawer onOpenChange={setDrawerControl} open={drawerControl}>
        <DrawerContent className="data-[side=right]:w-4/5 data-[side=right]:sm:max-w-xs" side="right">
          <DrawerHeader className="flex flex-row items-center justify-between gap-2 text-left">
            <DrawerTitle className="sr-only">{t('Main menu')}</DrawerTitle>
            <LogoFull justify="flex-start" size="sm" />
            <DrawerClose>
              <XIcon aria-hidden className="size-5" />
              <span className="sr-only">{t('Close')}</span>
            </DrawerClose>
          </DrawerHeader>

          <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-auto px-0">
            <SideNavItem icon={<HouseIcon />} label={t('Home')} onClick={closeDrawer} to="/feed" />
            <SideNavItem icon={<LibraryIcon />} label={t('Cliterature')} onClick={closeDrawer} to="/publication/list" />
            <SideNavItem icon={<MessagesSquareIcon />} label={t('Chat')} onClick={closeDrawer} to="/chat" />
            <SideNavItem icon={<CalendarDaysIcon />} label={t('Events')} onClick={closeDrawer} to="/event" />
            <SideNavItem icon={<ImagesIcon />} label={t('Gallery')} onClick={closeDrawer} to="/gallery" />
            <SideNavItem icon={<LayersIcon />} label={t('Groups')} onClick={closeDrawer} to="/group/list" />
            <SideNavItem icon={<MapPinIcon />} label={t('Places')} onClick={closeDrawer} to="/place" />
            <SideNavItem icon={<StoreIcon />} label={t('Vendor Market')} onClick={closeDrawer} to="/vendor-market" />
            <SideNavItem
              icon={<UsersRoundIcon />}
              label={t('Profile Search')}
              onClick={closeDrawer}
              to="/profile/list"
            />
            <SideNavItem icon={<CalendarIcon />} label={t('Rendezvous')} onClick={closeDrawer} to="/rendezvous" />
            {!isAtLeastRole(UserRole.SUPPORTER) && (
              <SideNavItem
                icon={<LogoSymbol imageClassName="w-5" />}
                label={t('Support Spicy Friends')}
                onClick={closeDrawer}
                to="/supporter"
              />
            )}
            {hasPermission('admin:access') && (
              <>
                <Separator className="w-full" />
                <SideNavItem icon={<MonitorCogIcon />} label={t('Admin')} onClick={closeDrawer} to="/admin/dashboard" />
                <Separator className="w-full" />
              </>
            )}
          </div>

          <DrawerFooter className="border-border border-t">
            <SeasonalEffectsControls
              christmasLightsEnabled={showChristmasLights}
              fogEnabled={showFog}
              onChristmasLightsToggle={(enabled) => {
                setShowChristmasLights(enabled)
                setSeasonalCookie('SeasonalChristmasLights', enabled, themeName)
              }}
              onFogToggle={(enabled) => {
                setShowFog(enabled)
                setSeasonalCookie('SeasonalFogEffect', enabled, themeName)
              }}
              onSnowflakeToggle={(enabled) => {
                setShowSnowflake(enabled)
                setSeasonalCookie('SeasonalSnowflake', enabled, themeName)
              }}
              onSpiderToggle={(enabled) => {
                setShowSpider(enabled)
                setSeasonalCookie('SeasonalSpiderWalking', enabled, themeName)
              }}
              snowflakeEnabled={showSnowflake}
              spiderEnabled={showSpider}
            />
            <PwaInstallButton className="w-full border border-muted-foreground border-dashed" variant="ghost" />
            {isAuthenticated && isSentryFeedbackEnabled() && (
              <>
                <button
                  aria-expanded={feedbackMenuOpen}
                  className="block w-full border-none bg-transparent p-0 text-left"
                  onClick={() => setFeedbackMenuOpen((open) => !open)}
                  type="button"
                >
                  <div className="relative mx-2 flex min-h-10 cursor-pointer items-center gap-3 overflow-hidden rounded-md px-3 text-muted-foreground transition-colors hover:bg-card hover:text-foreground">
                    <div className="relative flex shrink-0 items-center gap-2 [&_svg]:size-6">
                      <span aria-hidden className="h-7 w-0.5 shrink-0 self-center rounded-sm bg-transparent" />
                      <MessageSquarePlusIcon />
                    </div>
                    <span className="block min-w-0 flex-1 truncate font-medium text-lg md:text-base">
                      {t('Give Feedback')}
                    </span>
                    <ChevronRightIcon
                      aria-hidden
                      className={cn('size-4 shrink-0 transition-transform', feedbackMenuOpen && 'rotate-90')}
                    />
                  </div>
                </button>
                {feedbackMenuOpen && (
                  <div className="flex flex-col">
                    <button
                      className="mx-2 min-h-10 cursor-pointer rounded-md pr-3 pl-14 text-left font-medium text-lg text-muted-foreground hover:bg-card hover:text-foreground md:text-base"
                      onClick={() => {
                        closeDrawer()
                        openSentryFeedback({
                          kind: 'bug',
                          profileId: currentUser?.profile?.id,
                          profileName: currentUser?.profile?.name,
                          t,
                        })
                      }}
                      type="button"
                    >
                      {t('Bug Report')}
                    </button>
                    <button
                      className="mx-2 min-h-10 cursor-pointer rounded-md pr-3 pl-14 text-left font-medium text-lg text-muted-foreground hover:bg-card hover:text-foreground md:text-base"
                      onClick={() => {
                        closeDrawer()
                        openSentryFeedback({
                          kind: 'feedback',
                          profileId: currentUser?.profile?.id,
                          profileName: currentUser?.profile?.name,
                          t,
                        })
                      }}
                      type="button"
                    >
                      {t('Feature Request')}
                    </button>
                  </div>
                )}
              </>
            )}
            <SideNavItem
              icon={<LogOutIcon />}
              label={t('Sign out')}
              onClick={() => {
                closeDrawer()
                logOut.mutate()
              }}
            />
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  )
}

export default MainNavBar
